import { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { any, equals } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { getLabelMessage } from '@gef-ui/localization/messages';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { codelists } from 'constants/codelists';
import { USER_ROLES } from 'constants/general';
import {
	KCL_DISPLAY_DOCUMENT_CHECK,
	MPU_NOTIF_BACKOFFICE_TASK,
	MPU_NOTIF_ESC_LEAD_MARKET,
	MPU_NOTIF_ESC_LEAD_UNRESOLVED,
	MPU_NOTIF_ESC_REQ2SEL,
	MPU_NOTIF_SUMMARY_ESC_SUP,
	MPU_NOTIF_TASK_CREATION,
} from 'features/common/acl/constants';
import { notificationSetIds } from 'features/views/settings/constants';
import { getSwitchDescription } from 'features/views/settings/utils';
import { getCurrentRole } from 'features/views/user/selectors';
import { fetchAdditionalContextUserInfo } from 'features/views/userContext/actions';
import { getContextUser, getContextUserHasRole, hasContextPermissionById } from 'features/views/userContext/selectors';
import { getCodelistByName } from 'utils/codelists';

import SettingsSwitch from './SettingsSwitch';

const {
	TASK_CREATION,
	LEAD_ESCALATION,
	LEAD_ESCALATION_UNRESOLVED,
	REQ_2_SELL,
	SUMMARY_ESC_SUP,
	BACKOFFICE_UNRESOLVED,
	ASSIGNED_CONTACTS_ADMINISTRATION,
	ASSIGNED_CONTACTS_GROUP_MANAGER,
	ASSIGNED_CONTACTS_REGIONAL_MANAGER,
	PRODUCTION_CONTROL,
	PORTFOLIO_LEAD_CREATION,
	PORTFOLIO_LEAD_ESCALATION,
} = notificationSetIds;

export const AlwaysTurnedOnNotifications = () => {
	const dispatch = useDispatch();
	const contextUser = useSelector(getContextUser);

	const hasNotifTaskCreationPermission = useSelector((state) =>
		contextUser
			? hasContextPermissionById(state, MPU_NOTIF_TASK_CREATION)
			: hasPermissionByIds(state)(MPU_NOTIF_TASK_CREATION),
	);

	const hasNotifEscLeadMarketPermission = useSelector((state) =>
		contextUser
			? hasContextPermissionById(state, MPU_NOTIF_ESC_LEAD_MARKET)
			: hasPermissionByIds(state)(MPU_NOTIF_ESC_LEAD_MARKET),
	);

	const hasNotifEscLeadUnresolvedPermission = useSelector((state) =>
		contextUser
			? hasContextPermissionById(state, MPU_NOTIF_ESC_LEAD_UNRESOLVED)
			: hasPermissionByIds(state)(MPU_NOTIF_ESC_LEAD_UNRESOLVED),
	);

	const hasNotifReq2SellPermission = useSelector((state) =>
		contextUser
			? hasContextPermissionById(state, MPU_NOTIF_ESC_REQ2SEL)
			: hasPermissionByIds(state)(MPU_NOTIF_ESC_REQ2SEL),
	);

	const hasNotifSummaryEscSupPermission = useSelector((state) =>
		contextUser
			? hasContextPermissionById(state, MPU_NOTIF_SUMMARY_ESC_SUP)
			: hasPermissionByIds(state)(MPU_NOTIF_SUMMARY_ESC_SUP),
	);

	const hasNotifBackofficeTaskPermission = useSelector((state) =>
		contextUser
			? hasContextPermissionById(state, MPU_NOTIF_BACKOFFICE_TASK)
			: hasPermissionByIds(state)(MPU_NOTIF_BACKOFFICE_TASK),
	);

	const hasNotifProductionControlPermission = useSelector((state) =>
		contextUser
			? hasContextPermissionById(state, KCL_DISPLAY_DOCUMENT_CHECK)
			: hasPermissionByIds(state)(KCL_DISPLAY_DOCUMENT_CHECK),
	);

	const notificationsSetCodelist = useSelector(getCodelistByName(codelists.notificationsSet));
	const notificationsSetContactsCodelist = useSelector(getCodelistByName(codelists.notificationsSetContacts));

	const currentRole = useSelector(getCurrentRole);

	const isContextUserRegionalManager = useSelector((state) =>
		getContextUserHasRole(state, USER_ROLES.REGION_MANAGER),
	);
	const isContextUserAdministration = useSelector((state) => getContextUserHasRole(state, USER_ROLES.AGENCY_MANAGER));
	const isContextUserGroupManager = useSelector((state) => getContextUserHasRole(state, USER_ROLES.GROUP_MANAGER));

	const isRegionalDirector = contextUser ? isContextUserRegionalManager : currentRole === USER_ROLES.REGION_MANAGER;
	const isAdministration = contextUser ? isContextUserAdministration : currentRole === USER_ROLES.AGENCY_MANAGER;
	const isGroupManager = contextUser ? isContextUserGroupManager : currentRole === USER_ROLES.GROUP_MANAGER;

	const getSwitchShortDescription = getSwitchDescription(notificationsSetCodelist);
	const getContactsSwitchShortDescription = getSwitchDescription(notificationsSetContactsCodelist);

	const sectionPermissions = [
		hasNotifTaskCreationPermission,
		hasNotifEscLeadMarketPermission,
		hasNotifEscLeadUnresolvedPermission,
		hasNotifReq2SellPermission,
		hasNotifSummaryEscSupPermission,
		hasNotifBackofficeTaskPermission,
		isRegionalDirector,
		isAdministration,
		isGroupManager,
	];

	// has at least one of the defined permissions
	const showWholeSection = any(equals(true))(sectionPermissions);

	useEffect(() => {
		if (contextUser && showWholeSection) {
			dispatch(fetchAdditionalContextUserInfo(contextUser));
		}
	}, [dispatch, contextUser, showWholeSection]);

	const settingsHeader = (
		<Stack
			spacing={1}
			direction="row"
			sx={{
				alignItems: 'center',
			}}
		>
			<Typography variant="h5" color="text.primary">
				{getLabelMessage('settings:alwaysTurnedOnNotifications')}
			</Typography>
			<InfoCircleTooltip text={getLabelMessage('settings:alwaysTurnedOnNotificationsHeaderTooltip')} />
		</Stack>
	);

	return (
		showWholeSection && (
			<Grid size={12}>
				<Grid container>
					<Grid size={12}>{settingsHeader}</Grid>
					{hasNotifTaskCreationPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(TASK_CREATION)}
								notificationSetId={TASK_CREATION}
							/>
						</Grid>
					)}
					{hasNotifEscLeadMarketPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(PORTFOLIO_LEAD_CREATION)}
								notificationSetId={PORTFOLIO_LEAD_CREATION}
							/>
						</Grid>
					)}
					{hasNotifEscLeadMarketPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(LEAD_ESCALATION)}
								notificationSetId={LEAD_ESCALATION}
							/>
						</Grid>
					)}
					{hasNotifEscLeadMarketPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(PORTFOLIO_LEAD_ESCALATION)}
								notificationSetId={PORTFOLIO_LEAD_ESCALATION}
							/>
						</Grid>
					)}
					{hasNotifEscLeadUnresolvedPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(LEAD_ESCALATION_UNRESOLVED)}
								notificationSetId={LEAD_ESCALATION_UNRESOLVED}
							/>
						</Grid>
					)}
					{hasNotifReq2SellPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(REQ_2_SELL)}
								notificationSetId={REQ_2_SELL}
							/>
						</Grid>
					)}
					{hasNotifSummaryEscSupPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(SUMMARY_ESC_SUP)}
								notificationSetId={SUMMARY_ESC_SUP}
							/>
						</Grid>
					)}
					{hasNotifBackofficeTaskPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getSwitchShortDescription(BACKOFFICE_UNRESOLVED)}
								notificationSetId={BACKOFFICE_UNRESOLVED}
							/>
						</Grid>
					)}

					{isRegionalDirector && (
						<Grid size={12}>
							<SettingsSwitch
								description={getContactsSwitchShortDescription(ASSIGNED_CONTACTS_REGIONAL_MANAGER)}
								notificationSetId={ASSIGNED_CONTACTS_REGIONAL_MANAGER}
								doNotUseCodelistPermissionForSwitch
							/>
						</Grid>
					)}
					{isAdministration && (
						<Grid size={12}>
							<SettingsSwitch
								description={getContactsSwitchShortDescription(ASSIGNED_CONTACTS_ADMINISTRATION)}
								notificationSetId={ASSIGNED_CONTACTS_ADMINISTRATION}
								doNotUseCodelistPermissionForSwitch
							/>
						</Grid>
					)}
					{isGroupManager && (
						<Grid size={12}>
							<SettingsSwitch
								description={getContactsSwitchShortDescription(ASSIGNED_CONTACTS_GROUP_MANAGER)}
								notificationSetId={ASSIGNED_CONTACTS_GROUP_MANAGER}
								doNotUseCodelistPermissionForSwitch
							/>
						</Grid>
					)}
					{hasNotifProductionControlPermission && (
						<Grid size={12}>
							<SettingsSwitch
								description={getContactsSwitchShortDescription(PRODUCTION_CONTROL)}
								notificationSetId={PRODUCTION_CONTROL}
								doNotUseCodelistPermissionForSwitch
							/>
						</Grid>
					)}
				</Grid>
			</Grid>
		)
	);
};

export default AlwaysTurnedOnNotifications;
